import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  Megaphone, Plus, Pencil, Trash2, Power, PowerOff, Users,
  CheckCircle2, Clock3, ChevronDown, ChevronUp,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AUDIENCE_LABELS = { all: 'All staff', retail: 'Retail', event: 'Events' };

export const AnnouncementsManager = ({ crMode = false }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ title: '', message: '', audience: 'all' });
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [confirmData, setConfirmData] = useState({});
  const [deleteArmedId, setDeleteArmedId] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/announcements`);
      setRows(res.data || []);
    } catch (_e) {
      toast.error('Failed to load announcements');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const scrollToForm = () => setTimeout(() => {
    document.querySelector('[data-testid="announcement-form"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, 120);
  const openCreate = () => { setEditingId(null); setForm({ title: '', message: '', audience: 'all' }); setShowForm(true); scrollToForm(); };
  const openEdit = (r) => { setEditingId(r.id); setForm({ title: r.title, message: r.message, audience: r.audience }); setShowForm(true); scrollToForm(); };

  const save = async () => {
    if (!form.title.trim() || !form.message.trim()) { toast.error('Title and message are required'); return; }
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/admin/announcements/${editingId}`, form);
        toast.success('Announcement updated');
      } else {
        await axios.post(`${API}/admin/announcements`, form);
        toast.success('Announcement posted — staff must confirm it before using the portal');
      }
      setShowForm(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (r) => {
    try {
      await axios.post(`${API}/admin/announcements/${r.id}/${r.active ? 'deactivate' : 'activate'}`);
      toast.success(r.active ? 'Announcement deactivated' : 'Announcement activated');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    }
  };

  const remove = async (r) => {
    // In-app two-step confirm — window.confirm is silently blocked in some
    // mobile browsers / installed PWAs, which made Delete appear broken.
    if (deleteArmedId !== r.id) {
      setDeleteArmedId(r.id);
      toast.warning('Press the red button again to confirm deletion', { duration: 4000 });
      setTimeout(() => setDeleteArmedId((cur) => (cur === r.id ? null : cur)), 5000);
      return;
    }
    setDeleteArmedId(null);
    try {
      await axios.delete(`${API}/admin/announcements/${r.id}`);
      toast.success('Announcement deleted');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const toggleConfirmations = async (r) => {
    if (expandedId === r.id) { setExpandedId(null); return; }
    setExpandedId(r.id);
    try {
      const res = await axios.get(`${API}/admin/announcements/${r.id}/confirmations`);
      setConfirmData((prev) => ({ ...prev, [r.id]: res.data }));
    } catch (_e) {
      toast.error('Failed to load confirmations');
    }
  };

  return (
      <div className="space-y-6" data-testid="announcements-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-barlow text-3xl tracking-wide uppercase flex items-center gap-3">
              <Megaphone className="w-7 h-7 text-primary" /> Announcements
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Staff are blocked from using the portal until they press Confirm &amp; Understood.
            </p>
          </div>
          <Button onClick={openCreate} className="rounded-sm bg-primary text-primary-foreground hover:bg-yellow-300 uppercase tracking-wider text-xs" data-testid="new-announcement-btn">
            <Plus className="w-4 h-4 mr-2" /> New announcement
          </Button>
        </div>

        {showForm && (
          <div className="border border-border rounded-sm p-5 bg-card space-y-4" data-testid="announcement-form">
            <Input
              placeholder="Title (e.g. New uniform rules from Monday)"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="announcement-title-input"
            />
            <Textarea
              placeholder="Write the full announcement here…"
              rows={5}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              data-testid="announcement-message-input"
            />
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs uppercase tracking-wider text-muted-foreground">Audience:</span>
              {Object.entries(AUDIENCE_LABELS).map(([val, label]) => (
                <Button
                  key={val}
                  variant={form.audience === val ? 'default' : 'outline'}
                  size="sm"
                  className="rounded-sm text-xs uppercase"
                  onClick={() => setForm({ ...form, audience: val })}
                  data-testid={`audience-${val}-btn`}
                >
                  {label}
                </Button>
              ))}
            </div>
            <div className="flex gap-2">
              <Button onClick={save} disabled={saving} className="rounded-sm bg-primary text-primary-foreground hover:bg-yellow-300 uppercase tracking-wider text-xs" data-testid="save-announcement-btn">
                {saving ? 'Saving…' : editingId ? 'Save changes' : 'Post announcement'}
              </Button>
              <Button variant="outline" onClick={() => setShowForm(false)} className="rounded-sm text-xs uppercase" data-testid="cancel-announcement-btn">Cancel</Button>
            </div>
          </div>
        )}

        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="no-announcements">No announcements yet. Post one and every targeted staff member must confirm it at login.</p>
        ) : (
          <div className="space-y-4">
            {rows.map((r) => {
              const cd = confirmData[r.id];
              return (
                <div key={r.id} className={`border rounded-sm bg-card ${r.active ? 'border-primary' : 'border-border opacity-80'}`} data-testid={`announcement-${r.id}`}>
                  <div className="p-4 space-y-2">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold">{r.title}</h3>
                          {r.active
                            ? <Badge className="bg-green-600 text-white rounded-sm uppercase text-[10px]">Active</Badge>
                            : <Badge variant="outline" className="rounded-sm uppercase text-[10px]">Inactive</Badge>}
                          <Badge variant="outline" className="rounded-sm uppercase text-[10px]"><Users className="w-3 h-3 mr-1" />{AUDIENCE_LABELS[r.audience] || r.audience}</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{r.message}</p>
                        <p className="text-xs text-muted-foreground mt-2">
                          Posted {new Date(r.created_at).toLocaleString('en-GB')} · <strong>{r.confirmed_count}</strong> of <strong>{r.target_count}</strong> confirmed
                        </p>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <Button variant="outline" size="sm" className="rounded-sm" onClick={() => toggleActive(r)} data-testid={`toggle-active-${r.id}`} title={r.active ? 'Deactivate' : 'Activate'}>
                          {r.active ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                        </Button>
                        <Button variant="outline" size="sm" className="rounded-sm" onClick={() => openEdit(r)} data-testid={`edit-announcement-${r.id}`} title="Edit">
                          <Pencil className="w-4 h-4" />
                        </Button>
                        {!crMode && (
                        <Button
                          variant={deleteArmedId === r.id ? 'destructive' : 'outline'}
                          size="sm"
                          className={`rounded-sm ${deleteArmedId === r.id ? 'animate-pulse' : 'text-red-500 hover:text-red-400'}`}
                          onClick={() => remove(r)}
                          data-testid={`delete-announcement-${r.id}`}
                          title={deleteArmedId === r.id ? 'Press again to confirm' : 'Delete'}
                        >
                          <Trash2 className="w-4 h-4" />
                          {deleteArmedId === r.id && <span className="ml-1 text-xs">Confirm?</span>}
                        </Button>
                        )}
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" className="rounded-sm text-xs uppercase tracking-wider" onClick={() => toggleConfirmations(r)} data-testid={`view-confirmations-${r.id}`}>
                      {expandedId === r.id ? <ChevronUp className="w-4 h-4 mr-1" /> : <ChevronDown className="w-4 h-4 mr-1" />}
                      Who confirmed
                    </Button>
                    {expandedId === r.id && (
                      <div className="grid md:grid-cols-2 gap-4 pt-2" data-testid={`confirmations-panel-${r.id}`}>
                        <div className="border border-border rounded-sm p-3">
                          <p className="text-xs uppercase tracking-wider text-green-500 flex items-center gap-1 mb-2"><CheckCircle2 className="w-3.5 h-3.5" /> Confirmed ({cd ? cd.confirmed.length : '…'})</p>
                          {cd ? (cd.confirmed.length === 0 ? <p className="text-xs text-muted-foreground">Nobody yet.</p> : (
                            <ul className="space-y-1">
                              {cd.confirmed.map((c) => (
                                <li key={c.user_id} className="text-sm flex justify-between gap-2">
                                  <span>{c.user_name}</span>
                                  <span className="text-xs text-muted-foreground">{new Date(c.confirmed_at).toLocaleString('en-GB')}</span>
                                </li>
                              ))}
                            </ul>
                          )) : <p className="text-xs text-muted-foreground">Loading…</p>}
                        </div>
                        <div className="border border-border rounded-sm p-3">
                          <p className="text-xs uppercase tracking-wider text-amber-500 flex items-center gap-1 mb-2"><Clock3 className="w-3.5 h-3.5" /> Not confirmed yet ({cd ? cd.pending.length : '…'})</p>
                          {cd ? (cd.pending.length === 0 ? <p className="text-xs text-muted-foreground">Everyone confirmed 🎉</p> : (
                            <ul className="space-y-1">
                              {cd.pending.map((p) => (
                                <li key={p.user_id} className="text-sm">{p.user_name}</li>
                              ))}
                            </ul>
                          )) : <p className="text-xs text-muted-foreground">Loading…</p>}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
  );
};

export default AnnouncementsManager;
